<template>
  <div class="act-info">
    <app-state v-if="loading" state="loading" title="Загружаем акт сверки" />
    <template v-else>
      <page-section title="Период и стороны" description="Реквизиты сохранены в акте. Их можно уточнить без изменения операций и итогов.">
        <div class="act-info__parties">
          <div>
            <span>Наша организация</span>
            <strong>{{ organization.name || '—' }}</strong>
            <p>{{ joinValues(organization.inn && `ИНН ${organization.inn}`, organization.kpp && `КПП ${organization.kpp}`) }}</p>
            <p>{{ organization.address || '—' }}</p>
          </div>
          <div>
            <span>Контрагент</span>
            <strong>{{ counterparty.name || '—' }}</strong>
            <p>{{ joinValues(counterparty.inn && `ИНН ${counterparty.inn}`, counterparty.kpp && `КПП ${counterparty.kpp}`) }}</p>
            <p>{{ counterparty.address || counterparty.legal_address || '—' }}</p>
          </div>
        </div>
        <q-btn v-if="canEditRequisites" outline no-caps icon="edit" label="Редактировать реквизиты" data-testid="act-sver-edit-requisites" @click="openRequisites" />
        <p v-if="saveMessage" role="status">{{ saveMessage }}</p>
        <dl class="act-info__period"><div><dt>Период</dt><dd>{{ periodText }}</dd></div><div><dt>Дата формирования</dt><dd>{{ generatedAt }}</dd></div></dl>
      </page-section>

      <page-section title="Операции и обороты" description="Документы обеих сторон показаны как рабочая таблица, без имитации печатного листа.">
        <app-data-table
          title="Операции сверки"
          :rows="rows"
          :columns="columns"
          :count="rows.length"
          :state="rows.length ? 'ready' : 'empty'"
          state-title="Операций за период нет"
          state-message="Акт сформирован с нулевыми оборотами."
          storage-key="act-sver-info"
          data-testid="act-sver-rows"
        >
          <template #body-cell-my_debet="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
          <template #body-cell-my_credit="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
          <template #body-cell-contr_debet="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
          <template #body-cell-contr_credit="props"><q-td :props="props"><amount-cell :value="props.value" /></q-td></template>
        </app-data-table>
        <div class="act-info__totals">
          <div><span>Оборот, дебет</span><amount-cell :value="turnovers.my_debet" /></div>
          <div><span>Оборот, кредит</span><amount-cell :value="turnovers.my_credit" /></div>
          <div><span>Сальдо на начало, дебет</span><amount-cell :value="totalBefore.my_total_debet" /></div>
          <div><span>Сальдо на начало, кредит</span><amount-cell :value="totalBefore.my_total_credit" /></div>
          <div><span>Сальдо на конец, дебет</span><amount-cell :value="total.my_total_debet" /></div>
          <div><span>Сальдо на конец, кредит</span><amount-cell :value="total.my_total_credit" /></div>
        </div>
      </page-section>

      <q-banner v-if="diagnostics.length" rounded class="act-info__diagnostics" role="status">
        <strong>Диагностика формирования</strong>
        <ul><li v-for="item in diagnostics" :key="`${item.code}-${item.message}`">{{ item.message || item.code }}</li></ul>
      </q-banner>

      <page-section title="Подписи" description="Реквизиты подписантов, сохраненные вместе с актом.">
        <div class="act-info__signatures">
          <div><span>{{ organization.name || 'Наша организация' }}</span><strong>{{ organizationSignature }}</strong></div>
          <div><span>{{ counterparty.name || 'Контрагент' }}</span><strong>{{ counterpartySignature }}</strong></div>
        </div>
      </page-section>

    </template>

    <q-dialog v-model="editing" persistent @show="focusDiagnosticField">
      <q-card class="act-info__editor" data-testid="act-sver-requisites-editor">
        <q-card-section><h2 class="act-info__editor-title">Реквизиты акта сверки</h2><p>Изменения сохранятся только в этом акте. Операции, обороты и сальдо останутся прежними.</p></q-card-section>
        <q-form ref="requisitesForm" @submit.prevent="saveRequisites">
          <q-card-section class="act-info__editor-fields">
            <fieldset v-for="party in editorParties" :key="party.key">
              <legend>{{ party.label }}</legend>
              <q-input :ref="`${party.key}-name`" v-model="draft[party.key].name" outlined label="Наименование" :aria-label="`Наименование: ${party.label}`" />
              <q-input :ref="`${party.key}-inn`" v-model="draft[party.key].inn" outlined label="ИНН" :aria-label="`ИНН: ${party.label}`" inputmode="numeric" />
              <q-input :ref="`${party.key}-kpp`" v-model="draft[party.key].kpp" outlined label="КПП" :aria-label="`КПП: ${party.label}`" inputmode="numeric" />
              <q-input :ref="`${party.key}-ogrn`" v-model="draft[party.key].ogrn" outlined label="ОГРН / ОГРНИП" :aria-label="`ОГРН / ОГРНИП: ${party.label}`" inputmode="numeric" />
              <q-input :ref="`${party.key}-address`" v-model="draft[party.key].address" outlined label="Адрес" :aria-label="`Адрес: ${party.label}`" />
            </fieldset>
            <fieldset>
              <legend>Подписанты</legend>
              <q-input ref="organization-signer" v-model="draft.additional_data.my_fio_name" outlined label="ФИО подписанта нашей организации" />
              <q-input v-model="draft.additional_data.my_position" outlined label="Должность подписанта нашей организации" />
              <q-input ref="counterparty-signer" v-model="draft.additional_data.contr_fio_name" outlined label="ФИО подписанта контрагента" />
              <q-input v-model="draft.additional_data.contr_position" outlined label="Должность подписанта контрагента" />
            </fieldset>
            <p v-if="saveError" class="act-info__error" role="alert">{{ saveError }}</p>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn flat no-caps label="Отмена" :disable="saving" @click="editing = false" />
            <q-btn type="submit" color="primary" no-caps label="Сохранить реквизиты" :loading="saving" :disable="saving || !canEditRequisites" />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import AmountCell from '../../../../components/ui/AmountCell.vue'
import AppDataTable from '../../../../components/ui/AppDataTable.vue'
import AppState from '../../../../components/ui/AppState.vue'
import PageSection from '../../../../components/ui/PageSection.vue'
import { formatDate } from '../../../../services/dateFormat'

const EDITOR_PARTIES = Object.freeze([{ key: 'organization', label: 'Наша организация' }, { key: 'counterparty', label: 'Контрагент' }])
const emptyDraft = () => ({ organization: {}, counterparty: {}, additional_data: {} })

const EMPTY_TOTAL = Object.freeze({ my_total_debet: '', my_total_credit: '', contr_total_debet: '', contr_total_credit: '' })
const EMPTY_TURNOVERS = Object.freeze({ my_debet: '', my_credit: '', contr_debet: '', contr_credit: '' })
const columns = Object.freeze([
  { name: 'my_date', label: 'Дата', align: 'left', field: 'my_date' },
  { name: 'my_name', label: 'Документ по нашим данным', align: 'left', field: 'my_name', required: true },
  { name: 'my_debet', label: 'Дебет', align: 'right', field: 'my_debet' },
  { name: 'my_credit', label: 'Кредит', align: 'right', field: 'my_credit' },
  { name: 'contr_name', label: 'Документ контрагента', align: 'left', field: 'contr_name' },
  { name: 'contr_debet', label: 'Дебет контрагента', align: 'right', field: 'contr_debet' },
  { name: 'contr_credit', label: 'Кредит контрагента', align: 'right', field: 'contr_credit' }
])

export default {
  name: 'document_info_act_sver',
  components: { AmountCell, AppDataTable, AppState, PageSection },
  props: { embeddedWorkbench: { type: Boolean, default: false } },
  data() {
    return {
      body: {},
      draft: emptyDraft(),
      editorParties: EDITOR_PARTIES,
      editing: false,
      saving: false,
      saveError: '',
      saveMessage: '',
      saveRequestId: null,
      pendingDiagnostic: null,
      diagnosticTarget: '',
      isActive: false,
      loading: true,
      infoRequestId: null,
      handlers: [],
      columns
    }
  },
  computed: {
    canEditRequisites() {
      return this.$store.state.permissions.edit_fin_documents === true && this.$store.state.connection_status !== 'offline' &&
        (!this.$route.params.history_id || this.$route.params.history_id === 'last') && this.isActive
    },
    rows() { return Array.isArray(this.body.rows) ? this.body.rows : [] },
    turnovers() { return this.body.turnovers || EMPTY_TURNOVERS },
    totalBefore() { return this.body.total_before || EMPTY_TOTAL },
    total() { return this.body.total || EMPTY_TOTAL },
    diagnostics() { return Array.isArray(this.body.diagnostics) ? this.body.diagnostics : [] },
    organization() { return this.body.organization || {} },
    counterparty() { return this.body.counterparty || this.body.counterparty_snapshot || {} },
    periodText() {
      const period = this.body.period || {}
      const start = period.start_display || this.safeDate(this.body.period_start)
      const end = period.end_display || this.safeDate(this.body.period_end)
      return start && end ? `${start} — ${end}` : '—'
    },
    generatedAt() {
      if (!this.body.generated_at) return this.safeDate(this.body.document_date) || '—'
      const parsed = moment(this.body.generated_at)
      return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : '—'
    },
    organizationSignature() {
      if (this.body.additional_data?.my_fio_name) return [this.body.additional_data.my_position, this.body.additional_data.my_fio_name].filter(Boolean).join(', ')
      const signature = this.body.signatures?.organization || this.body.organization_signature
      return this.signatureText(signature, [this.body.additional_data?.my_position, this.body.additional_data?.my_fio_name || this.body.rukvod].filter(Boolean).join(', '))
    },
    counterpartySignature() {
      if (this.body.additional_data?.contr_fio_name) return [this.body.additional_data.contr_position, this.body.additional_data.contr_fio_name].filter(Boolean).join(', ')
      const signature = this.body.signatures?.counterparty || this.body.counterparty_signature
      return this.signatureText(signature, [this.body.additional_data?.contr_position, this.body.additional_data?.contr_fio_name].filter(Boolean).join(', '))
    }
  },
  mounted() {
    this.listen('didReceiveDocumentInfo', this.onDocumentInfo)
    this.listen('onUpdateActSverRequisites', this.onRequisitesSaved)
    this.listen('connectionReady', this.loadDocument)
    if (this.connection.isAuthenticated) this.loadDocument()
  },
  beforeUnmount() { this.handlers.forEach(([event, handler]) => this.emitter.off(event, handler)) },
  methods: {
    listen(event, handler) { this.emitter.on(event, handler); this.handlers.push([event, handler]) },
    loadDocument() {
      this.loading = true
      this.infoRequestId = this.connection.makeRequestId('act-sver-info')
      this.connection.getDocumentInfo(this.infoRequestId, this.$route.params.id, this.$route.params.history_id || 'last')
    },
    onDocumentInfo(response) {
      if (response.request_id !== this.infoRequestId || response.data?.document_type !== 'act_sver') return
      this.body = response.data.body || {}
      this.isActive = response.data.is_active !== false
      this.loading = false
      if (this.pendingDiagnostic) {
        const diagnostic = this.pendingDiagnostic
        this.pendingDiagnostic = null
        this.focusArtifactDiagnostic(diagnostic)
      }
    },
    openRequisites() {
      const party = (value) => Object.fromEntries(['name', 'inn', 'kpp', 'ogrn', 'address'].map((field) => [field, String(value[field] || (field === 'address' && value.legal_address) || '')]))
      const additional = this.body.additional_data || {}
      this.draft = {
        organization: party(this.organization),
        counterparty: party(this.counterparty),
        additional_data: {
          my_fio_name: additional.my_fio_name || additional.organization_signer || '',
          my_position: additional.my_position || '',
          contr_fio_name: additional.contr_fio_name || additional.counterparty_signer || '',
          contr_position: additional.contr_position || ''
        }
      }
      this.saveError = ''
      this.saveMessage = ''
      this.diagnosticTarget = ''
      this.editing = true
    },
    saveRequisites() {
      if (this.saving || !this.canEditRequisites) return
      this.saveError = ''
      this.saving = true
      this.saveRequestId = this.connection.makeRequestId('act-sver-requisites')
      this.connection.updateActSverRequisites(this.saveRequestId, this.$route.params.id, {
        organization: this.draft.organization,
        counterparty: this.draft.counterparty,
        additional_data: {
          ...this.draft.additional_data,
          my_org_name: this.draft.organization.name,
          contr_org_name: this.draft.counterparty.name
        }
      })
    },
    onRequisitesSaved(response) {
      if (response.request_id !== this.saveRequestId) return
      this.saving = false
      if (response.type === 'error' || response.error) {
        this.saveError = response.message || response.error || 'Не удалось сохранить реквизиты'
        return
      }
      this.editing = false
      this.saveMessage = 'Реквизиты акта сохранены'
      this.loadDocument()
    },
    async focusArtifactDiagnostic(diagnostic) {
      if (this.loading) { this.pendingDiagnostic = diagnostic; return }
      const path = diagnostic.fieldPath || diagnostic.field_path || ''
      const match = path.match(/^act_sver\.(organization|counterparty)\.(name|inn|kpp|ogrn|address)$/)
      const signer = path.match(/^act_sver\.signatures\.(organization|counterparty)\.name$/)
      const ref = match ? `${match[1]}-${match[2]}` : (signer ? `${signer[1]}-signer` : '')
      if (!ref || !this.canEditRequisites) return
      this.openRequisites()
      this.diagnosticTarget = ref
    },
    focusDiagnosticField() {
      if (!this.diagnosticTarget) return
      const field = this.$refs[this.diagnosticTarget]
      const input = Array.isArray(field) ? field[0] : field
      input?.focus()
    },
    safeDate(value) { return value ? formatDate(value) : '' },
    joinValues(...values) { return values.filter(Boolean).join(' / ') || '—' },
    signatureText(signature, fallback = '') {
      if (typeof signature === 'string') return signature || 'Не указаны'
      if (signature && typeof signature === 'object') return [signature.position, signature.name || signature.fio].filter(Boolean).join(', ') || 'Не указаны'
      return fallback || 'Не указаны'
    },
    onDeleteDocument() { this.connection.deleteDocument(this.connection.makeRequestId('act-sver-delete'), this.$route.params.id) },
    onRestoreDocument() { this.connection.restoreDocument(this.connection.makeRequestId('act-sver-restore'), this.$route.params.id) }
  }
}
</script>

<style scoped>
.act-info { display: grid; gap: var(--sid-space-6); min-width: 0; }
.act-info__parties { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-6); }
.act-info__parties > div { display: grid; gap: var(--sid-space-1); min-width: 0; }
.act-info__parties span, .act-info__period dt, .act-info__totals span, .act-info__signatures span { color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.act-info__parties p { margin: 0; overflow-wrap: anywhere; }
.act-info__period { display: flex; flex-wrap: wrap; gap: var(--sid-space-6); margin: var(--sid-space-4) 0 0; }
.act-info__period dd { margin: var(--sid-space-1) 0 0; }
.act-info__totals { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--sid-space-4); margin-top: var(--sid-space-5); }
.act-info__totals div, .act-info__signatures div { display: grid; gap: var(--sid-space-1); }
.act-info__diagnostics { background: var(--sid-color-warning-surface); }
.act-info__diagnostics ul { margin: var(--sid-space-2) 0 0; padding-left: var(--sid-space-5); }
.act-info__editor { width: 48rem; max-width: 100%; }
.act-info__editor-title { font-size: var(--sid-font-size-section); margin: 0; }
.act-info__editor-fields { display: grid; gap: var(--sid-space-5); }
.act-info__editor-fields fieldset { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-3); border: 1px solid var(--sid-color-line); min-width: 0; padding: var(--sid-space-3); }
.act-info__editor-fields legend { padding: 0 var(--sid-space-2); }
.act-info__error { color: var(--sid-color-danger); margin: 0; }
.act-info__signatures { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-6); }
@media (max-width: 599px) {
  .act-info__editor-fields fieldset, .act-info__parties, .act-info__totals, .act-info__signatures { grid-template-columns: 1fr; }
}
</style>
